import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  TrendingUp,
  Search,
  Target,
  Lightbulb,
  Building2,
  Users,
  ArrowRight,
  BarChart3,
  Sparkles,
  DollarSign,
  Video
} from 'lucide-react';

interface CategoryData {
  name: string;
  searchVolume: number;
  cpc: number;
  competition: string;
  demandScore: number;
  trend: string | null;
}

interface Opportunity {
  category: string;
  categoryPath: string; // Full Google taxonomy path
  searchVolume: number;
  cpc: number;
  competition: string;
  demandScore: number;
  suggestion: string;
}

interface TopBrand {
  brand: string;
  catalogCount: number;
  reason: string;
}

export interface MarketIntelligenceData {
  sector: string;
  lastAnalyzed: string | null;
  topCategories: CategoryData[];
  opportunities: Opportunity[];
  topBrands: TopBrand[];
  customerIntents: string[];
  summary: {
    totalCategories: number;
    opportunitiesCount: number;
    highPriorityOpportunities: number;
    hasAEOContent: boolean;
  };
}

interface MarketIntelligencePanelProps {
  data: MarketIntelligenceData | null;
  loading?: boolean;
}

function formatSearchVolume(vol: number): string {
  if (vol >= 1000000) {
    return `${(vol / 1000000).toFixed(1)}M`;
  }
  if (vol >= 1000) {
    return `${(vol / 1000).toFixed(0)}K`;
  }
  return vol.toString();
}

function getCompetitionBadge(competition: string) {
  const colors: Record<string, string> = {
    high: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400',
    medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/50 dark:text-yellow-400',
    low: 'bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-400'
  };
  return colors[competition?.toLowerCase()] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
}

export function MarketIntelligencePanel({ data, loading }: MarketIntelligencePanelProps) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-500" />
            Market Intelligence
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <div className="animate-pulse text-muted-foreground">Loading market data...</div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-8">
          <TrendingUp className="h-10 w-10 text-muted-foreground mb-3" />
          <h4 className="font-semibold mb-1">Market Intelligence Unavailable</h4>
          <p className="text-sm text-muted-foreground text-center">
            No sector data available for this site. Contact support to enable market intelligence.
          </p>
        </CardContent>
      </Card>
    );
  }

  const maxSearchVolume = Math.max(...data.topCategories.map(c => c.searchVolume), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-500" />
            Market Intelligence
            <Badge variant="outline" className="ml-2 font-normal">
              {data.sector}
            </Badge>
          </CardTitle>
          <CardDescription>
            Search volume data and content opportunities for your sector
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Content Opportunities - Most Important for Content Managers */}
      {data.opportunities.length > 0 && (
        <Card className="border-orange-200 bg-orange-50/50 dark:border-orange-900 dark:bg-orange-950/30">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lightbulb className="h-5 w-5 text-orange-500" />
              Content Opportunities
              <Badge className="ml-2 bg-orange-500">{data.opportunities.length}</Badge>
            </CardTitle>
            <CardDescription className="text-orange-700 dark:text-orange-300">
              High-demand categories without AEO content - create these to capture search traffic
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.opportunities.map((opp, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 rounded-lg bg-white dark:bg-card border dark:border-border"
              >
                <div className="flex items-start gap-3 flex-1">
                  <div className={`p-2 rounded-full ${
                    opp.searchVolume > 50000
                      ? 'bg-red-100 dark:bg-red-950/50'
                      : 'bg-orange-100 dark:bg-orange-950/50'
                  }`}>
                    <Search className={`h-4 w-4 ${
                      opp.searchVolume > 50000 ? 'text-red-600 dark:text-red-400' : 'text-orange-600 dark:text-orange-400'
                    }`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium truncate text-foreground">{opp.category}</h4>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                      <span className="flex items-center gap-1">
                        <Search className="h-3 w-3" />
                        {formatSearchVolume(opp.searchVolume)}/mo
                      </span>
                      {opp.cpc > 0 && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          ${parseFloat(opp.cpc as unknown as string).toFixed(2)} CPC
                        </span>
                      )}
                      <Badge variant="outline" className={getCompetitionBadge(opp.competition)}>
                        {opp.competition}
                      </Badge>
                    </div>
                    <p className="text-xs text-orange-700 dark:text-orange-400 mt-1">{opp.suggestion}</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => navigate(`/about-business/offering?tab=categories&category=${encodeURIComponent(opp.categoryPath || opp.category)}`)}
                >
                  Create AEO
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Top Categories by Search Volume */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="h-5 w-5 text-blue-500" />
            Top Categories by Search Volume
          </CardTitle>
          <CardDescription>
            Most searched categories in your sector - prioritize content for these
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.topCategories.slice(0, 6).map((cat, index) => (
            <div key={index} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{cat.name}</span>
                  {cat.trend && (
                    <Badge variant="outline" className={
                      cat.trend.startsWith('+') ? 'text-green-600' : 'text-red-600'
                    }>
                      {cat.trend}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-muted-foreground">
                    {formatSearchVolume(cat.searchVolume)}/mo
                  </span>
                  <Badge variant="outline" className={getCompetitionBadge(cat.competition)}>
                    {cat.competition}
                  </Badge>
                </div>
              </div>
              <Progress
                value={(cat.searchVolume / maxSearchVolume) * 100}
                className="h-2"
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Two Column Layout: Brands & Customer Intents */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Brands */}
        {data.topBrands.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="h-5 w-5 text-purple-500" />
                Top Brands in Your Catalog
              </CardTitle>
              <CardDescription>
                Leading brands to feature in your content
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.topBrands.slice(0, 6).map((brand, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 dark:from-purple-900/50 dark:to-blue-900/50 flex items-center justify-center text-xs font-bold text-purple-700 dark:text-purple-300">
                        {index + 1}
                      </div>
                      <span className="font-medium">{brand.brand}</span>
                    </div>
                    <Badge variant="secondary">
                      {brand.catalogCount} products
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customer Intents */}
        {data.customerIntents.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="h-5 w-5 text-green-500" />
                Customer Search Intents
              </CardTitle>
              <CardDescription>
                What customers are looking for - address these in your content
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {data.customerIntents.map((intent, index) => (
                  <Badge
                    key={index}
                    variant="outline"
                    className="bg-green-50 border-green-200 text-green-700 dark:bg-green-950/50 dark:border-green-800 dark:text-green-400 py-1.5 px-3"
                  >
                    <Target className="h-3 w-3 mr-1" />
                    {intent}
                  </Badge>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-2">
                  Use these intents to guide your content strategy
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin/experts')}
                >
                  <Video className="h-3 w-3 mr-1" />
                  Create Product Expert Interview
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Summary Stats */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 border-blue-100 dark:border-blue-900">
        <CardContent className="py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {data.summary.totalCategories}
              </div>
              <div className="text-xs text-muted-foreground">Sector Categories</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {data.summary.opportunitiesCount}
              </div>
              <div className="text-xs text-muted-foreground">Without AEO</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {data.summary.highPriorityOpportunities}
              </div>
              <div className="text-xs text-muted-foreground">High Volume</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {data.summary.hasAEOContent ? 'Yes' : 'No'}
              </div>
              <div className="text-xs text-muted-foreground">Has AEO Content</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
